import { useEffect, useMemo, useState } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { AlertTriangle, BookOpen, Check, RefreshCw, Radio, Sparkles, Square, Trash2, X } from 'lucide-react'

function formatWhen(value) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch (_) {
    return String(value)
  }
}

function cohortLabel(session) {
  if (!session) return 'this cohort'
  if (session.intakeId) {
    return session.intakeName || session.intakeCode || `Intake ${session.intakeId}`
  }
  return 'the whole course (course-wide)'
}

function renderInlineMarkdown(text, keyPrefix) {
  if (!text) return null
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={`${keyPrefix}-b-${i}`} className="text-white font-semibold">
          {part.slice(2, -2)}
        </strong>
      )
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code
          key={`${keyPrefix}-c-${i}`}
          className="px-1.5 py-0.5 bg-black/50 border border-white/10 font-mono text-[0.75rem] text-cinema-gold rounded-sm"
        >
          {part.slice(1, -1)}
        </code>
      )
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return (
        <em key={`${keyPrefix}-i-${i}`} className="text-cinema-soft italic">
          {part.slice(1, -1)}
        </em>
      )
    }
    return <span key={`${keyPrefix}-t-${i}`}>{part}</span>
  })
}

function parseMarkdownBlocks(rawContent) {
  if (!rawContent) return []
  const normalized = String(rawContent).replace(/\r\n/g, '\n')
  const lines = normalized.split('\n')
  const blocks = []
  let currentBlock = null

  function flush() {
    if (!currentBlock) return
    if (currentBlock.type === 'paragraph' && currentBlock.lines.length) {
      blocks.push(currentBlock)
    } else if (currentBlock.type === 'blockquote' && currentBlock.lines.length) {
      blocks.push(currentBlock)
    } else if (currentBlock.type === 'ul' && currentBlock.items.length) {
      blocks.push(currentBlock)
    } else if (currentBlock.type === 'ol' && currentBlock.items.length) {
      blocks.push(currentBlock)
    }
    currentBlock = null
  }

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i]
    const trimmed = rawLine.trim()

    if (trimmed === '') {
      flush()
      continue
    }

    if (/^---+$|^\*\*\*+$|^___+$/.test(trimmed)) {
      flush()
      blocks.push({ type: 'hr' })
      continue
    }

    const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/)
    if (headingMatch) {
      flush()
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length,
        text: headingMatch[2].trim(),
      })
      continue
    }

    const bqMatch = trimmed.match(/^>\s*(.*)$/)
    if (bqMatch) {
      if (!currentBlock || currentBlock.type !== 'blockquote') {
        flush()
        currentBlock = { type: 'blockquote', lines: [bqMatch[1]] }
      } else {
        currentBlock.lines.push(bqMatch[1])
      }
      continue
    }

    const ulMatch = trimmed.match(/^[-*•]\s+(.+)$/)
    if (ulMatch) {
      if (!currentBlock || currentBlock.type !== 'ul') {
        flush()
        currentBlock = { type: 'ul', items: [ulMatch[1]] }
      } else {
        currentBlock.items.push(ulMatch[1])
      }
      continue
    }

    const olMatch = trimmed.match(/^\d+[.)]\s+(.+)$/)
    if (olMatch) {
      if (!currentBlock || currentBlock.type !== 'ol') {
        flush()
        currentBlock = { type: 'ol', items: [olMatch[1]] }
      } else {
        currentBlock.items.push(olMatch[1])
      }
      continue
    }

    if (!currentBlock || currentBlock.type !== 'paragraph') {
      flush()
      currentBlock = { type: 'paragraph', lines: [rawLine.trimEnd()] }
    } else {
      currentBlock.lines.push(rawLine.trimEnd())
    }
  }

  flush()
  return blocks
}

function DraftMarkdownViewer({ content }) {
  if (!content) return null
  const blocks = parseMarkdownBlocks(content)

  return (
    <div className="space-y-3.5 font-sans text-xs sm:text-sm leading-relaxed text-cinema-soft max-w-3xl">
      {blocks.map((block, bIdx) => {
        if (block.type === 'hr') {
          return <hr key={`hr-${bIdx}`} className="border-t border-white/10 my-4" />
        }

        if (block.type === 'heading') {
          if (block.level === 1) {
            return (
              <h3 key={`h1-${bIdx}`} className="font-display text-base sm:text-lg text-white font-medium pt-3 pb-1 border-b border-white/10">
                {renderInlineMarkdown(block.text, `h1-${bIdx}`)}
              </h3>
            )
          }
          if (block.level === 2) {
            return (
              <h4 key={`h2-${bIdx}`} className="font-sans text-sm sm:text-base text-cinema-gold font-semibold pt-3 pb-0.5">
                {renderInlineMarkdown(block.text, `h2-${bIdx}`)}
              </h4>
            )
          }
          return (
            <h5 key={`h3-${bIdx}`} className="font-sans text-xs sm:text-sm text-white font-medium pt-2">
              {renderInlineMarkdown(block.text, `h3-${bIdx}`)}
            </h5>
          )
        }

        if (block.type === 'blockquote') {
          return (
            <blockquote key={`bq-${bIdx}`} className="border-l-2 border-cinema-gold/40 pl-3 py-1 bg-white/[0.01] text-cinema-soft italic text-[0.85rem]">
              {block.lines.map((l, i) => (
                <p key={`bql-${bIdx}-${i}`}>{renderInlineMarkdown(l, `bql-${bIdx}-${i}`)}</p>
              ))}
            </blockquote>
          )
        }

        if (block.type === 'ul') {
          return (
            <ul key={`ul-${bIdx}`} className="list-disc pl-5 space-y-1.5 marker:text-cinema-glow text-cinema-soft">
              {block.items.map((item, i) => (
                <li key={`li-${bIdx}-${i}`} className="pl-1">
                  {renderInlineMarkdown(item, `li-${bIdx}-${i}`)}
                </li>
              ))}
            </ul>
          )
        }

        if (block.type === 'ol') {
          return (
            <ol key={`ol-${bIdx}`} className="list-decimal pl-5 space-y-1.5 marker:text-cinema-gold text-cinema-soft">
              {block.items.map((item, i) => (
                <li key={`nli-${bIdx}-${i}`} className="pl-1">
                  {renderInlineMarkdown(item, `nli-${bIdx}-${i}`)}
                </li>
              ))}
            </ol>
          )
        }

        if (block.type === 'paragraph') {
          return (
            <p key={`p-${bIdx}`} className="text-xs sm:text-sm leading-relaxed text-cinema-soft">
              {block.lines.map((line, lIdx) => (
                <span key={`pl-${bIdx}-${lIdx}`}>
                  {lIdx > 0 && <br />}
                  {renderInlineMarkdown(line, `p-${bIdx}-${lIdx}`)}
                </span>
              ))}
            </p>
          )
        }

        return null
      })}
    </div>
  )
}

export default function TrainerClassControl({ courseId, courseTitle, courseCode }) {
  const [lessons, setLessons] = useState([])
  const [intakes, setIntakes] = useState([])
  const [openSessions, setOpenSessions] = useState([])
  const [drafts, setDrafts] = useState([])
  const [staleSignals, setStaleSignals] = useState([])
  const [lessonId, setLessonId] = useState('')
  const [intakeId, setIntakeId] = useState('')
  const [copilotLessonId, setCopilotLessonId] = useState('')
  const [alreadyOpen, setAlreadyOpen] = useState(null)
  const [highlightedSessionId, setHighlightedSessionId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [opening, setOpening] = useState(false)
  const [closingId, setClosingId] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [approvingId, setApprovingId] = useState(null)
  const [regeneratingId, setRegeneratingId] = useState(null)
  const [discardingId, setDiscardingId] = useState(null)
  const [discardTarget, setDiscardTarget] = useState(null)

  const selectedOpenSession = useMemo(() => {
    if (!openSessions.length) return null
    if (highlightedSessionId) {
      return openSessions.find((row) => row.id === highlightedSessionId) || openSessions[0]
    }
    return openSessions[0]
  }, [openSessions, highlightedSessionId])

  const load = async () => {
    const [controlRes, draftsRes] = await Promise.all([
      api.get(`/trainers/courses/${courseId}/class-control`),
      api.get(`/trainers/courses/${courseId}/copilot/drafts`),
    ])
    const nextLessons = controlRes.data.lessons || []
    const nextOpen = controlRes.data.openSessions || []
    setLessons(nextLessons)
    setIntakes(controlRes.data.intakes || [])
    setOpenSessions(nextOpen)
    setDrafts(draftsRes.data.drafts || [])
    setStaleSignals(draftsRes.data.staleSignals || [])
    setLessonId((current) => current || (nextLessons[0] ? String(nextLessons[0].id) : ''))
    setCopilotLessonId((current) => {
      if (current) return current
      if (nextOpen[0]?.lessonId) return String(nextOpen[0].lessonId)
      return nextLessons[0] ? String(nextLessons[0].id) : ''
    })
    return { nextOpen }
  }

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        setLoading(true)
        await load()
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.error || 'Failed to load class controls')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [courseId])

  const openClass = async (event) => {
    event.preventDefault()
    if (!lessonId) {
      toast.error('Select a lesson from this course')
      return
    }
    setOpening(true)
    setAlreadyOpen(null)
    try {
      const res = await api.post(`/trainers/courses/${courseId}/teaching-sessions`, {
        lessonId: Number(lessonId),
        intakeId: intakeId ? Number(intakeId) : null,
      })
      toast.success('Class opened')
      setHighlightedSessionId(res.data.session?.id || null)
      if (res.data.session?.lessonId) setCopilotLessonId(String(res.data.session.lessonId))
      await load()
    } catch (err) {
      const payload = err.response?.data || {}
      if (err.response?.status === 409 && payload.code === 'TEACHING_SESSION_ALREADY_OPEN') {
        setAlreadyOpen(payload.openSession || true)
        if (payload.openSession?.id) setHighlightedSessionId(payload.openSession.id)
        toast.error('A class is already open for this cohort')
        await load()
      } else {
        toast.error(payload.error || 'Could not open class')
      }
    } finally {
      setOpening(false)
    }
  }

  const closeClass = async (session) => {
    if (!session?.id) return
    setClosingId(session.id)
    try {
      await api.post(`/trainers/teaching-sessions/${session.id}/close`)
      toast.success('Class closed')
      if (alreadyOpen && (alreadyOpen === true || alreadyOpen.id === session.id)) {
        setAlreadyOpen(null)
      }
      const { nextOpen } = await load()
      if (highlightedSessionId === session.id) {
        setHighlightedSessionId(nextOpen[0]?.id || null)
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not close class')
    } finally {
      setClosingId(null)
    }
  }

  const generateDraft = async () => {
    const sessionLesson = selectedOpenSession?.lessonId
    const chosenLesson = sessionLesson || copilotLessonId
    if (!chosenLesson) {
      toast.error('Select a lesson, or open a class first')
      return
    }
    setGenerating(true)
    try {
      const res = await api.post(`/trainers/courses/${courseId}/copilot/drafts`, {
        lessonId: Number(chosenLesson),
        teachingSessionId: selectedOpenSession?.id || null,
      })
      toast.success('Draft notes ready — still proposed until you approve')
      await load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not generate draft notes')
    } finally {
      setGenerating(false)
    }
  }

  const approveDraft = async (draft) => {
    if (!draft?.id || draft.status !== 'proposed') return
    const confirmed = window.confirm(
      'Approve these teaching notes for this lesson? Once approved, they become available to ground the Student Tutor for this lesson.'
    )
    if (!confirmed) return
    setApprovingId(draft.id)
    try {
      await api.post(`/trainers/courses/${courseId}/copilot/drafts/${draft.id}/approve`)
      toast.success('Draft approved and made available to Student Tutor')
      await load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not approve draft')
    } finally {
      setApprovingId(null)
    }
  }

  const regenerateDraft = async (draft) => {
    if (!draft?.id || draft.status !== 'proposed' || regeneratingId) return
    setRegeneratingId(draft.id)
    try {
      const sameLessonOpen = selectedOpenSession?.lessonId === draft.lessonId ? selectedOpenSession.id : null
      await api.post(`/trainers/courses/${courseId}/copilot/drafts`, {
        lessonId: draft.lessonId,
        teachingSessionId: sameLessonOpen,
      })
      toast.success('New draft version generated — the previous proposed version was superseded')
      await load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not regenerate draft')
    } finally {
      setRegeneratingId(null)
    }
  }

  const confirmDiscard = async () => {
    const draft = discardTarget
    if (!draft?.id) return
    setDiscardingId(draft.id)
    try {
      await api.post(`/trainers/courses/${courseId}/copilot/drafts/${draft.id}/discard`)
      toast.success('Draft discarded')
      setDiscardTarget(null)
      await load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not discard draft')
    } finally {
      setDiscardingId(null)
    }
  }

  if (loading) {
    return (
      <p className="font-sans text-xs text-cinema-muted">Loading class controls…</p>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="mb-3">
          <h3 className="font-sans text-base text-white font-medium">Open a class</h3>
        </div>
        <p className="font-sans text-xs sm:text-sm text-cinema-muted mb-4 leading-relaxed">
          Pick an official lesson from this course. Intake is optional — leave it blank for a course-wide class.
          Students are not joined automatically yet.
        </p>

        {alreadyOpen && (
          <div
            id="already-open-class"
            className="mb-4 border border-amber-400/40 bg-amber-400/10 px-4 py-3 space-y-2"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle size={15} className="text-amber-300 mt-0.5 shrink-0" />
              <div>
                <p className="font-sans text-xs sm:text-sm text-white font-medium">
                  A class is already open for this cohort
                </p>
                <p className="font-sans text-xs text-cinema-muted mt-0.5 leading-relaxed">
                  {alreadyOpen !== true
                    ? `${alreadyOpen.lessonTitle || 'A lesson'} · ${cohortLabel(alreadyOpen)}. Close it before opening another class for the same cohort.`
                    : 'Close the live class below, then try opening again. A second live class for the same cohort is not allowed.'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  const target = document.getElementById('open-classes')
                  if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className="cinema-btn-ghost text-xs px-3 py-1.5 min-h-0"
              >
                View open class
              </button>
              {alreadyOpen !== true && alreadyOpen.id && (
                <button
                  type="button"
                  onClick={() => closeClass(alreadyOpen)}
                  disabled={closingId === alreadyOpen.id}
                  className="cinema-btn-primary text-xs px-3 py-1.5 min-h-0"
                >
                  {closingId === alreadyOpen.id ? 'Closing…' : 'Close that class'}
                </button>
              )}
            </div>
          </div>
        )}

        <form onSubmit={openClass} className="grid gap-3 md:grid-cols-3 items-end">
          <label className="block">
            <span className="cinema-label block mb-1">Lesson</span>
            <select
              value={lessonId}
              onChange={(event) => setLessonId(event.target.value)}
              className="w-full bg-white/5 border border-white/10 text-white text-xs px-3 py-2"
            >
              <option value="">Select a lesson</option>
              {lessons.map((lesson) => (
                <option key={lesson.id} value={lesson.id}>
                  {lesson.moduleTitle ? `${lesson.moduleTitle} · ` : ''}{lesson.title}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="cinema-label block mb-1">Intake (optional)</span>
            <select
              value={intakeId}
              onChange={(event) => setIntakeId(event.target.value)}
              className="w-full bg-white/5 border border-white/10 text-white text-xs px-3 py-2"
            >
              <option value="">Course-wide</option>
              {intakes.map((intake) => (
                <option key={intake.id} value={intake.id}>
                  {intake.name}{intake.code ? ` (${intake.code})` : ''}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={opening || !lessonId}
            className="cinema-btn-primary text-xs px-4 py-2 min-h-0 inline-flex"
          >
            <Radio size={12} />
            {opening ? 'Opening…' : 'Open class'}
          </button>
        </form>
      </div>

      <div id="open-classes">
        <div className="mb-3">
          <h3 className="font-sans text-sm text-white font-medium">Open classes</h3>
        </div>
        {openSessions.length === 0 ? (
          <p className="font-sans text-xs sm:text-sm text-cinema-muted">
            No live classes at the moment. Pick a course, module, lesson, and cohort above to start one.
          </p>
        ) : (
          <div className="space-y-2">
            {openSessions.map((session) => (
              <div
                key={session.id}
                className={`p-3.5 border flex items-center justify-between gap-3 ${
                  highlightedSessionId === session.id
                    ? 'border-cinema-gold/50 bg-cinema-gold/5'
                    : 'border-white/8 bg-white/[0.02]'
                }`}
                onClick={() => setHighlightedSessionId(session.id)}
              >
                <div>
                  <p className="font-sans text-sm text-white font-medium">
                    {session.lessonTitle || 'Lesson not set'}
                  </p>
                  <p className="font-sans text-xs text-cinema-muted mt-0.5">
                    {cohortLabel(session)} · opened {formatWhen(session.startedAt)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => closeClass(session)}
                  disabled={closingId === session.id}
                  className="cinema-btn-ghost text-xs sm:text-sm px-3 py-1.5 min-h-0 inline-flex"
                >
                  <Square size={13} />
                  {closingId === session.id ? 'Closing…' : 'Close'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mb-3">
          <h3 className="font-sans text-base text-white font-medium">Trainer Copilot</h3>
        </div>
        <p className="font-sans text-xs sm:text-sm text-cinema-muted mb-4 leading-relaxed">
          Draft notes for the open class, or for a lesson you select if nothing is live.
          Generate always creates a new version. Approve a specific draft to make it available to the Student Tutor.
          Students only see approved notes — never proposed or superseded drafts.
        </p>
        {staleSignals.length > 0 && (
          <div className="mb-4 border border-amber-400/40 bg-amber-400/10 px-4 py-3 space-y-1.5">
            <p className="font-sans text-xs sm:text-sm text-white font-medium">
              Students are still seeing older approved notes
            </p>
            {staleSignals.map((signal) => (
              <p key={signal.proposedDraftId} className="font-sans text-xs text-cinema-muted leading-relaxed">
                {signal.message} Approve draft #{signal.proposedDraftId}
                {signal.proposedTitle ? ` (${signal.proposedTitle})` : ''} to replace approved #{signal.approvedDraftId}.
              </p>
            ))}
          </div>
        )}
        {selectedOpenSession ? (
          <p className="font-sans text-xs sm:text-sm text-cinema-glow mb-3">
            Using open class: {selectedOpenSession.lessonTitle || 'lesson'} · {cohortLabel(selectedOpenSession)}
          </p>
        ) : (
          <label className="block mb-3 max-w-md">
            <span className="cinema-label block mb-1">Lesson to prepare</span>
            <select
              value={copilotLessonId}
              onChange={(event) => setCopilotLessonId(event.target.value)}
              className="w-full bg-white/5 border border-white/10 text-white text-xs sm:text-sm px-3 py-2"
            >
              <option value="" className="bg-cinema-charcoal text-white">Select a lesson…</option>
              {lessons.map((lesson) => (
                <option key={lesson.id} value={lesson.id} className="bg-cinema-charcoal text-white">
                  {lesson.moduleTitle ? `${lesson.moduleTitle} — ` : ''}{lesson.title}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          onClick={generateDraft}
          disabled={generating}
          className="cinema-btn-primary text-xs sm:text-sm px-4 py-2.5 min-h-0 disabled:opacity-40 inline-flex items-center gap-2"
        >
          <Sparkles size={14} />
          {generating ? 'Drafting…' : 'Generate draft notes'}
        </button>

        <div className="mt-6 space-y-5">
          {drafts.length === 0 ? (
            <p className="font-sans text-xs sm:text-sm text-cinema-muted flex items-center gap-2">
              <BookOpen size={14} /> No Copilot drafts yet.
            </p>
          ) : (
            drafts.map((draft) => {
              const associatedLesson = lessons.find((l) => l.id === draft.lessonId)
              const isProposed = draft.status === 'proposed'
              const isApproved = draft.status === 'approved'
              const isDiscarded = draft.status === 'discarded'

              return (
                <article
                  key={draft.id}
                  className={`border transition-all ${
                    isProposed
                      ? 'border-amber-400/30 bg-amber-400/[0.02]'
                      : isApproved
                        ? 'border-emerald-500/30 bg-emerald-500/[0.02]'
                        : isDiscarded
                          ? 'border-red-500/20 bg-red-500/[0.02]'
                          : 'border-white/5 bg-white/[0.01]'
                  } p-5 space-y-4 rounded-sm`}
                >
                  {/* Draft Header & Hierarchy */}
                  <div className="pb-3 border-b border-white/8 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="space-y-1.5 flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wider rounded-sm border ${
                            isProposed
                              ? 'bg-amber-400/10 text-amber-300 border-amber-400/30'
                              : isApproved
                                ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                                : isDiscarded
                                  ? 'bg-red-500/10 text-red-300 border-red-500/30'
                                  : 'bg-white/5 text-cinema-muted border-white/10'
                          }`}
                        >
                          {isProposed
                            ? 'PROPOSED — TRAINER REVIEW'
                            : isApproved
                              ? 'APPROVED — AVAILABLE TO STUDENT TUTOR'
                              : isDiscarded
                                ? 'DISCARDED — TRAINER REJECTED'
                                : 'SUPERSEDED'}
                        </span>
                        <span className="font-sans text-xs text-cinema-muted font-medium">
                          Draft #{draft.id}
                          {draft.supersedesDraftId ? ` (replaces #${draft.supersedesDraftId})` : ''}
                        </span>
                      </div>

                      <div className="pt-1 text-xs sm:text-sm text-cinema-muted space-y-0.5">
                        {courseTitle && (
                          <p className="font-sans text-xs sm:text-sm text-cinema-muted">
                            <span className="text-cinema-muted/60">Course:</span> {courseTitle}
                            {courseCode ? ` (${courseCode})` : ''}
                          </p>
                        )}
                        {associatedLesson?.moduleTitle && (
                          <p className="font-sans text-xs sm:text-sm text-cinema-muted">
                            <span className="text-cinema-muted/60">Module:</span> {associatedLesson.moduleTitle}
                          </p>
                        )}
                        <p className="font-sans text-sm text-white font-medium">
                          <span className="text-cinema-muted/60 font-normal">Lesson:</span>{' '}
                          {associatedLesson?.title || draft.title.replace(/^Draft notes:\s*/i, '')}
                        </p>
                      </div>

                      <p className="font-sans text-xs text-cinema-muted/80 pt-0.5 leading-relaxed">
                        {isProposed
                          ? 'Trainer review required before this material can be used academically. Student Tutor cannot access these notes until approved.'
                          : isApproved
                            ? `Approved on ${formatWhen(draft.approvedAt)}. These notes actively ground the Student Tutor for this lesson.`
                            : isDiscarded
                              ? 'Rejected by the trainer. Kept for history only — never available to students or the Student Tutor.'
                              : `Archived version superseded on ${formatWhen(draft.supersededAt)}.`}
                      </p>
                    </div>

                    {isProposed && (
                      <div className="shrink-0 flex flex-wrap items-center gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => regenerateDraft(draft)}
                          disabled={regeneratingId === draft.id || discardingId === draft.id}
                          className="cinema-btn-ghost text-xs px-3 py-2 min-h-0 inline-flex"
                          title="Generate a new proposed version for this lesson"
                        >
                          <RefreshCw size={13} />
                          {regeneratingId === draft.id ? 'Regenerating…' : 'Regenerate'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDiscardTarget(draft)}
                          disabled={discardingId === draft.id || regeneratingId === draft.id}
                          className="text-xs px-3 py-2 min-h-0 inline-flex items-center gap-1.5 border border-red-500/30 text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                          title="Reject this draft — it will not be made available to students"
                        >
                          <Trash2 size={13} />
                          {discardingId === draft.id ? 'Discarding…' : 'Discard'}
                        </button>
                        <button
                          type="button"
                          onClick={() => approveDraft(draft)}
                          disabled={approvingId === draft.id}
                          className="cinema-btn-primary text-xs px-4 py-2 min-h-0 inline-flex shadow-sm"
                          title="Approve these notes to make them active for the Student Tutor"
                        >
                          <Check size={13} />
                          {approvingId === draft.id ? 'Approving…' : 'Approve & Make Available'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Formatted Markdown Content */}
                  <div className="py-2">
                    <DraftMarkdownViewer content={draft.body} />
                  </div>
                </article>
              )
            })
          )}
        </div>
      </div>

      {discardTarget && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="cinema-glass-strong border border-white/10 rounded-sm w-full max-w-sm p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <h4 className="font-sans text-sm text-white font-medium">Discard this draft?</h4>
              <button
                type="button"
                onClick={() => setDiscardTarget(null)}
                className="text-cinema-muted hover:text-white shrink-0"
                aria-label="Cancel"
              >
                <X size={16} />
              </button>
            </div>
            <p className="font-sans text-xs text-cinema-muted">
              Discarded drafts are kept for history but will not be available to students or the Student Tutor.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setDiscardTarget(null)}
                disabled={discardingId === discardTarget.id}
                className="cinema-btn-ghost text-xs px-3 py-2 min-h-0"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDiscard}
                disabled={discardingId === discardTarget.id}
                className="text-xs px-3 py-2 min-h-0 inline-flex items-center gap-1.5 bg-red-500/90 hover:bg-red-500 text-white transition-colors disabled:opacity-50"
              >
                <Trash2 size={13} />
                {discardingId === discardTarget.id ? 'Discarding…' : 'Discard Draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
